// Marco de la Consola: riel de modulos, panel del modulo, barra superior, isla del telefono, hoja de cierre, buscador,
// dialogos y avisos. Aqui se conectan los atajos de teclado y los eventos del telefono.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { refrescoPorEventos } from '../data/vivo';
import { escribiendo, useKey, usePhone } from '../hooks';
import { instalarDesbloqueo } from '../lib/audio';
import { Icon } from '../lib/icons';
import { Island } from '../phone/Island';
import { PRESENCIAS, phone, type Presencia } from '../phone/store';
import { TipificarInterna, WrapSheet } from '../phone/WrapSheet';
import { abrirDialogo, Dialogos } from '../ui/forms';
import { toast, Toasts } from '../ui/toasts';
import { CommandPalette } from './CommandPalette';
import { Campana } from '../ui/Campana';
import { Chat } from '../ui/Chat';
import { FichaEntrante } from '../ui/FichaEntrante';
import { alRecibir, anunciarEstado, conectarTiempoReal, marcarDesde3cx } from '../lib/tiempoReal';
import { AsistenteFlotante } from '../ui/AsistenteFlotante';
import { Balizas } from '../ui/Balizas';
import { useFaviconAviso } from '../lib/faviconAviso';
import { useNavegacion } from './navegacion';
import { PanelModulo, SeccionesMovil, usaPanel } from './PanelModulo';
import { Riel } from './Riel';
import { CronometroBarra } from '../ui/HorasForm';

// El timbre necesita un clic previo en la pagina (Chrome): el primero tras entrar lo desbloquea (D68).
instalarDesbloqueo();

export function Shell() {
  // Tiempo real (D71): presencia del equipo y chat al instante. El estado del telefono se comparte con el equipo.
  useEffect(() => conectarTiempoReal(), []);
  // Icono de la pestaña con el color elegido y el numero de avisos cuando la Consola esta en segundo plano.
  useFaviconAviso();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const s = usePhone();
  const enLlamada = !!s.call && s.call.estado === 'activa';
  useEffect(() => {
    anunciarEstado(enLlamada ? 'en-llamada' : s.presencia, enLlamada ? 'en una llamada' : '');
  }, [enLlamada, s.presencia]);
  // Estado del agente sincronizado con 3CX por el Puente XmarteK (D78).
  useEffect(
    () =>
      alRecibir((e) => {
        if (e.tipo === 'estado3cx' && typeof e.estado === 'string' && e.estado in PRESENCIAS) {
          marcarDesde3cx();
          phone.setPresencia(e.estado as Presencia);
          toast(`Tu estado cambió en 3CX: ${PRESENCIAS[e.estado as Presencia].nombre}`);
        } else if (e.tipo === 'aviso3cx' && typeof e.mensaje === 'string') toast(e.mensaje, 'error');
      }),
    [],
  );
  // Llamadas que terminan en 3CX (aviso del tiempo real): se refrescan las llamadas y los contadores al instante.
  useEffect(() => {
    const r = refrescoPorEventos(qc);
    const quitar = alRecibir(r.alAviso);
    return () => (quitar(), r.parar());
  }, [qc]);
  const center = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [nuevoDe, setNuevoDe] = useState<DOMRect | null>(null);
  const cerrarPaleta = useCallback(() => setPaleta(false), []);
  const { actual } = useNavegacion();
  const conPanel = usaPanel(actual?.modulo);

  // Eventos del telefono -> interfaz.
  useEffect(
    () =>
      phone.onEvento((e) => {
        if (e.tipo === 'toast') toast(e.texto, e.icono);
        if (e.tipo === 'abrir-ficha') nav(`/personas/${e.personaId}`);
        if (e.tipo === 'registrada') {
          void qc.invalidateQueries({ queryKey: ['llamadas'] });
          void qc.invalidateQueries({ queryKey: ['tareas'] });
        }
      }),
    [nav, qc],
  );

  // Conectar el telefono al entrar.
  useEffect(() => {
    if (phone.getState().config.autoConectar) phone.connect();
  }, []);

  // Volver arriba al cambiar de pantalla.
  useEffect(() => {
    center.current?.scrollTo({ top: 0 });
  }, [loc.pathname]);

  // Cualquier enlace tel: de la Consola se marca desde la isla.
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      void phone.dial(decodeURIComponent(a.getAttribute('href')!.slice(4)));
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, []);

  // Advertir antes de cerrar la pestana con una llamada en curso.
  useEffect(() => {
    if (!s.call) return;
    const fn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [s.call]);

  useKey((e) => {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setPaleta((v) => !v);
      return;
    }
    if (paleta || escribiendo(e) || mod || e.altKey) return;
    if (document.querySelector('.dialog')) return;
    const st = phone.getState();
    const c = st.call;
    const k = e.key.toLowerCase();
    if (e.key === '/') {
      e.preventDefault();
      setPaleta(true);
      return;
    }
    if (e.key === 'Escape') {
      if (st.isla === 'panel') phone.collapse();
      return;
    }
    if (c?.estado === 'sonando') {
      if (k === 'a') (e.preventDefault(), phone.answer());
      if (k === 'r') (e.preventDefault(), phone.reject());
      return;
    }
    if (c) {
      if (k === 'm') phone.toggleMute();
      else if (k === 'h') phone.toggleHold();
      else if (k === 'k') phone.setVista('keypad');
      else if (k === 't') phone.setVista('transfer');
      else if (k === 'x') phone.hangup();
      else if (k === 'f' && c.contacto) nav(`/personas/${c.contacto.id}`);
      else if (/^[0-9*#]$/.test(e.key) && c.estado === 'activa') phone.dtmf(e.key);
      return;
    }
    if (k === 'p') phone.toggle();
  });

  return (
    <div className={`app ${conPanel ? 'con-panel' : ''}`}>
      <Riel />
      <PanelModulo />
      <main className={`center ${scrolled ? 'scrolled' : ''}`} ref={center} onScroll={(e) => setScrolled((e.target as HTMLElement).scrollTop > 4)}>
        <div className="ctop">
          <button className="search-btn" onClick={() => setPaleta(true)} aria-label="Buscar o marcar" data-testid="abrir-buscador">
            <Icon name="search" />
            <span>Buscar contacto, empresa o marcar número…</span>
            <kbd className="kbdh">Ctrl K</kbd>
          </button>
          <div className="island-slot" aria-hidden />
          <div className="sp" />
          <CronometroBarra />
          <Chat />
          <Campana />
          {s.config.modo === 'demo' ? (
            <button className="btn" onClick={() => phone.simularEntrante()} data-testid="simular">
              <Icon name="phoneIn" />
              <span className="lbl-m">Simular llamada entrante</span>
            </button>
          ) : null}
          <button className="btn primary" onClick={(e) => setNuevoDe(e.currentTarget.getBoundingClientRect())} aria-haspopup="menu">
            <Icon name="plus" />
            <span className="lbl-m">Nuevo</span>
          </button>
        </div>
        <SeccionesMovil />
        <Outlet />
      </main>

      <Island />
      <WrapSheet />
      <TipificarInterna />
      <FichaEntrante />
      <Dialogos />
      <Toasts />
      <AsistenteFlotante />
      <Balizas />
      {paleta ? <CommandPalette onClose={cerrarPaleta} /> : null}
      {nuevoDe
        ? createPortal(
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 69 }} onClick={() => setNuevoDe(null)} />
              <div className="menu" style={{ top: nuevoDe.bottom + 6, right: Math.max(8, window.innerWidth - nuevoDe.right) }} role="menu">
                {(
                  [
                    ['Contacto', 'user', () => abrirDialogo({ tipo: 'persona' })],
                    ['Empresa', 'building', () => abrirDialogo({ tipo: 'empresa' })],
                    ['Oportunidad', 'kanban', () => abrirDialogo({ tipo: 'oportunidad' })],
                    ['Tarea', 'task', () => abrirDialogo({ tipo: 'tarea' })],
                  ] as const
                ).map(([n, i, fn]) => (
                  <button key={n} role="menuitem" onClick={() => (setNuevoDe(null), fn())}>
                    <Icon name={i} /> {n}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
